import React, { useState, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { RootState, store } from "../store";
import { logout } from "../store/auth";
import logo from "@surgia/ui/icons/sidebar/logo.svg";
import cathlabLogo from "@surgia/ui/icons/sidebar/CathLab Logo-01.png";
import patternUrl from "@surgia/ui/icons/backgrounds/pattern.svg";
import Button from "@surgia/ui/components/button";
import { useTranslation } from "react-i18next";
import {
  authenticateUser,
  handleAuthenticationError,
} from "@surgia/api/src";
import { getTenant } from "@surgia/api/src/admin/tenant";
import { getStorefrontCurrencyLookup } from "@surgia/api/src/shared/lookups";

const LoginPage: React.FC = () => {
  const auth = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { globalState } = useSelector((state: RootState) => state.auth);
  const isReduxAuthenticated =
    globalState?.access_token && globalState?.subject_id;

  const from = location.state?.from?.pathname || "/";

  useEffect(() => {
    if (auth.isAuthenticated || isReduxAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [auth.isAuthenticated, isReduxAuthenticated, navigate, from]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const user = await auth.signinResourceOwnerCredentials({
        username,
        password,
      });

      if (!user) {
        setError(t("shared.loginFailed") || "Login failed. Please try again.");
        setIsSubmitting(false);
        return;
      }

      const keycloakTokens = {
        access_token: user.access_token,
        id_token: user.id_token ?? "",
        refresh_token: user.refresh_token ?? "",
        expires_at: user.expires_at ?? 0,
      };

      localStorage.setItem("token", user.access_token);

      const authResult = await authenticateUser(keycloakTokens);

      if (authResult.errorMessageKey === "Access denied") {
        store.dispatch(logout());
        localStorage.removeItem("token");
        sessionStorage.clear();
        window.location.href = "/wrong-portal";
        return;
      } else if (!authResult?.hasAccess) {
        handleAuthenticationError(store, logout);
        const keycloakLogoutUrl = `${import.meta.env.VITE_KEYCLOAK_ISSUER}/protocol/openid-connect/logout?client_id=${import.meta.env.VITE_KEYCLOAK_CLIENT_ID}&post_logout_redirect_uri=${window.location.origin}`;
        window.location.href = keycloakLogoutUrl;
        return;
      }

      const tenant_id = authResult?.userInfo?.user_profile?.tenant?.id;

      let storefront_id: number | undefined;
      if (tenant_id) {
        try {
          const tenantResponse = await getTenant({
            tenant_id,
            status: "ACTIVE",
            component_id: Number(import.meta.env.VITE_HUB_COMPONENT_ID),
          });
          storefront_id = tenantResponse?.data?.[0]?.storefront_id;
        } catch {
          // Continue without storefront_id
        }
      }

      let currency_code: string | undefined;
      let exchange_rate: number | undefined;
      if (storefront_id) {
        try {
          const currencyResponse = await getStorefrontCurrencyLookup({
            storefront_id: Number(storefront_id),
            is_base_currency: true,
          });
          const currencyInfo = currencyResponse?.[0];
          currency_code = currencyInfo?.currency?.code;
          exchange_rate = currencyInfo?.exchange_rate;
        } catch {
          // Continue with defaults
        }
      }

      const processedLastAccessedBranch = (() => {
        const lastAccessedBranch =
          authResult?.userInfo?.user_info?.last_accessed_branch;
        if (lastAccessedBranch === "allBranches") {
          const userBranches =
            authResult?.userInfo?.user_info?.user_branches || [];
          return userBranches.map((branch: any) => branch.id).join(",");
        }
        return lastAccessedBranch;
      })();

      localStorage.setItem(
        "lastAccessedBranch",
        JSON.stringify(processedLastAccessedBranch)
      );

      const authDataPayload = {
        ...keycloakTokens,
        userInfo: {
          email: authResult.userInfo?.user_info?.email,
          first_name: authResult.userInfo?.user_info?.first_name,
          last_name: authResult.userInfo?.user_info?.last_name,
          full_name: authResult.userInfo?.user_info?.full_name,
          mobile: authResult.userInfo?.user_info?.mobile,
          subject_id: authResult.userInfo?.user_info?.subject_id,
          creation_date: authResult.userInfo?.user_info?.creation_date,
          last_accessed_branch_id: processedLastAccessedBranch,
          my_profile: authResult.userInfo?.user_info?.my_profile,
          user_groups: authResult.userInfo?.user_info?.user_groups,
          last_login: authResult.userInfo?.user_info?.last_login,
          user_permissions: authResult.userInfo?.user_permissions,
          user_branches: authResult.userInfo?.user_info?.user_branches,
        },
        isAuthenticated: true,
        tenant_id,
        storefront_id: storefront_id ?? null,
        currency_code: currency_code ?? null,
        exchange_rate: exchange_rate ?? null,
      };

      sessionStorage.setItem("pendingAuthData", JSON.stringify(authDataPayload));

      window.location.href = from || "/";
    } catch {
      setError(t("shared.invalidCredentials") || "Invalid username or password.");
      setIsSubmitting(false);
    }
  };

  if (auth.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const appType = import.meta.env.VITE_APP_TYPE;
  const isCathlab = appType === "cathlab";
  const logoSrc = isCathlab ? cathlabLogo : logo;
  const logoAlt = isCathlab ? "CathLab Logo" : "Surgia Logo";

  return (
    <div
      className="relative min-h-screen flex items-center justify-center bg-white overflow-hidden"
      style={{
        backgroundImage: `url(${patternUrl})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}
    >
      <main className="relative z-10 flex flex-col items-center justify-center min-h-screen">
        <img
          src={logoSrc}
          width={200}
          alt={logoAlt}
          style={{
            maxWidth: "400px",
            height: "auto",
            ...(isCathlab ? {} : { aspectRatio: "256/83" }),
          }}
        />
        <form
          onSubmit={handleLogin}
          className="mt-8 flex flex-col gap-4 w-full max-w-sm"
        >
          <input
            type="text"
            autoComplete="username"
            placeholder={t("shared.username") || "Username"}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            className="w-full rounded-md border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <input
            type="password"
            autoComplete="current-password"
            placeholder={t("shared.password") || "Password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full rounded-md border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {error && (
            <p className="text-sm text-red-600 text-center">{error}</p>
          )}
          <Button
            type="submit"
            variant="ghost"
            className="cursor-pointer"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t("shared.signingIn") || "Signing in…"
              : t("shared.signIn")}
          </Button>
        </form>
      </main>
    </div>
  );
};

export default LoginPage;
